/**
 * AdminOnboardingCopy — sysadmin editor for the acclimation copy (Skip
 * request 2026-07-07; onboarding_copy, migration 265).
 *
 * Edits the welcome screen, welcome nudge, Getting-started rows, and the
 * promotion notice. Values are OVERRIDES over the static i18n defaults —
 * clearing a field restores the shipped text. English-only editing here
 * (system-admin UI convention); the JSONB translations column exists for
 * per-locale overrides when translation passes happen.
 *
 * "Preview welcome screen" opens the real WelcomeScreenModal with the
 * current effective copy and writes NO flags — the no-signup test path.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from '../../i18n/config';
import {
  fetchOnboardingCopy,
  saveOnboardingCopy,
  type OnboardingCopyEntry,
} from '../../api/onboardingCopy';
import WelcomeScreenModal from '../../Components/WelcomeScreenModal/WelcomeScreenModal';

interface CatalogEntry {
  /** Namespace-qualified override key (matches resolveCopyOverride). */
  key: string;
  /** i18n lookup used for the shipped default (namespace:short). */
  i18nKey: string;
  label: string;
  hint?: string;
  multiline?: boolean;
}

const CATALOG: CatalogEntry[] = [
  { key: 'pages:welcomeScreen.title', i18nKey: 'pages:welcomeScreen.title', label: 'Welcome screen — title (accessibility label)' },
  { key: 'pages:welcomeScreen.invitedEyebrow', i18nKey: 'pages:welcomeScreen.invitedEyebrow', label: 'Welcome screen — invited eyebrow', hint: '{{org}} = inviting organization name. Shown above the wordmark.' },
  { key: 'pages:welcomeScreen.welcomeEyebrow', i18nKey: 'pages:welcomeScreen.welcomeEyebrow', label: 'Welcome screen — eyebrow (no inviting org)' },
  { key: 'pages:welcomeScreen.body', i18nKey: 'pages:welcomeScreen.body', label: 'Welcome screen — body', hint: 'Blank line = new paragraph. Supports **bold** and "- bullet" lines.', multiline: true },
  { key: 'pages:welcomeScreen.bullet1', i18nKey: 'pages:welcomeScreen.bullet1', label: 'Welcome screen — bullet 1', hint: 'Save a single "-" to hide the row (clearing restores the default).' },
  { key: 'pages:welcomeScreen.bullet2', i18nKey: 'pages:welcomeScreen.bullet2', label: 'Welcome screen — bullet 2' },
  { key: 'pages:welcomeScreen.bullet3', i18nKey: 'pages:welcomeScreen.bullet3', label: 'Welcome screen — bullet 3' },
  { key: 'pages:welcomeNudge.title', i18nKey: 'pages:welcomeNudge.title', label: 'Org About nudge — title', hint: '{{org}} = organization name.' },
  { key: 'pages:welcomeNudge.body', i18nKey: 'pages:welcomeNudge.body', label: 'Org About nudge — body', multiline: true },
  { key: 'pages:welcomeNudge.cta', i18nKey: 'pages:welcomeNudge.cta', label: 'Org About nudge — button' },
  { key: 'pages:actionCenter.inbox.gettingStarted.readMeFirst.label', i18nKey: 'pages:actionCenter.inbox.gettingStarted.readMeFirst.label', label: 'Checklist — Read me first (title)' },
  { key: 'pages:actionCenter.inbox.gettingStarted.readMeFirst.nudge', i18nKey: 'pages:actionCenter.inbox.gettingStarted.readMeFirst.nudge', label: 'Checklist — Read me first (detail)', multiline: true },
  { key: 'pages:onboarding.milestones.complete_profile.label', i18nKey: 'pages:onboarding.milestones.complete_profile.label', label: 'Checklist — Complete profile (title)' },
  { key: 'pages:onboarding.nudges.explorer.completeProfile', i18nKey: 'pages:onboarding.nudges.explorer.completeProfile', label: 'Checklist — Complete profile (detail)', multiline: true },
  { key: 'pages:actionCenter.inbox.gettingStarted.followThree.label', i18nKey: 'pages:actionCenter.inbox.gettingStarted.followThree.label', label: 'Checklist — Follow 3 (title)' },
  { key: 'pages:actionCenter.inbox.gettingStarted.followThree.nudge', i18nKey: 'pages:actionCenter.inbox.gettingStarted.followThree.nudge', label: 'Checklist — Follow 3 (detail)', multiline: true },
  { key: 'pages:actionCenter.inbox.gettingStarted.standards.label', i18nKey: 'pages:actionCenter.inbox.gettingStarted.standards.label', label: 'Checklist — Community standards (title)' },
  { key: 'pages:actionCenter.inbox.gettingStarted.standards.nudge', i18nKey: 'pages:actionCenter.inbox.gettingStarted.standards.nudge', label: 'Checklist — Community standards (detail)', multiline: true },
  { key: 'pages:actionCenter.inbox.gettingStarted.meetOrg.label', i18nKey: 'pages:actionCenter.inbox.gettingStarted.meetOrg.label', label: 'Checklist — Meet your organization (title)', hint: '{{org}} = organization name.' },
  { key: 'pages:actionCenter.inbox.gettingStarted.meetOrg.nudge', i18nKey: 'pages:actionCenter.inbox.gettingStarted.meetOrg.nudge', label: 'Checklist — Meet your organization (detail)', multiline: true },
  { key: 'pages:actionCenter.inbox.gettingStarted.readGuidelines.label', i18nKey: 'pages:actionCenter.inbox.gettingStarted.readGuidelines.label', label: 'Checklist — Org guidelines (title)', hint: '{{org}} = organization name.' },
  { key: 'pages:actionCenter.inbox.gettingStarted.readGuidelines.nudge', i18nKey: 'pages:actionCenter.inbox.gettingStarted.readGuidelines.nudge', label: 'Checklist — Org guidelines (detail)', multiline: true },
  { key: 'pages:actionCenter.inbox.gettingStarted.promotion.subject', i18nKey: 'pages:actionCenter.inbox.gettingStarted.promotion.subject', label: 'Promotion notice — title' },
  { key: 'pages:actionCenter.inbox.gettingStarted.promotion.body', i18nKey: 'pages:actionCenter.inbox.gettingStarted.promotion.body', label: 'Promotion notice — body', multiline: true },
  // DEV-1441 — first-post Voice moment ribbon (variant B).
  { key: 'pages:feed.voiceMoment.title', i18nKey: 'pages:feed.voiceMoment.title', label: 'Voice moment — title', hint: 'One-time ribbon on a user\'s first-ever feed post.' },
  { key: 'pages:feed.voiceMoment.body', i18nKey: 'pages:feed.voiceMoment.body', label: 'Voice moment — body', multiline: true },
];

const S: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 860 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 },
  previewBtn: {
    fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 6,
    border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', color: '#0f172a',
  },
  note: { fontSize: 12, color: '#64748b' },
  row: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px' },
  rowHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 6 },
  label: { fontSize: 13, fontWeight: 600, color: '#0f172a' },
  keyText: { fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' },
  defaultText: { fontSize: 12, color: '#64748b', margin: '0 0 8px', whiteSpace: 'pre-wrap' },
  input: {
    width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '7px 10px',
    border: '1px solid #cbd5e1', borderRadius: 6, background: '#f8fafc', color: '#0f172a',
  },
  actions: { display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' },
  saveBtn: {
    fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 6,
    border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer',
  },
  clearBtn: {
    fontSize: 12, padding: '6px 12px', borderRadius: 6,
    border: '1px solid #cbd5e1', background: '#fff', color: '#475569', cursor: 'pointer',
  },
  status: { fontSize: 12, color: '#16a34a' },
  hint: { fontSize: 11, color: '#94a3b8', marginTop: 4 },
};

export default function AdminOnboardingCopy() {
  const [overrides, setOverrides] = useState<Map<string, OnboardingCopyEntry>>(new Map());
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Shipped English defaults, straight from the bundled locale (admin UI is
  // English-only by convention).
  const tEn = useMemo(() => i18n.getFixedT('en'), []);

  const load = useCallback(async () => {
    try {
      const rows = await fetchOnboardingCopy();
      const map = new Map<string, OnboardingCopyEntry>();
      const d: Record<string, string> = {};
      for (const r of rows) {
        map.set(r.copyKey, r);
        d[r.copyKey] = r.value;
      }
      setOverrides(map);
      setDrafts(d);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load copy overrides.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (key: string, value: string) => {
    setBusyKey(key);
    setError(null);
    try {
      await saveOnboardingCopy(key, value);
      await load();
      setSavedKey(key);
      window.setTimeout(() => setSavedKey((k) => (k === key ? null : k)), 2500);
    } catch (err: any) {
      setError(err?.message || 'Save failed.');
    } finally {
      setBusyKey(null);
    }
  }, [load]);

  return (
    <div style={S.wrap}>
      <div style={S.toolbar}>
        <button type="button" style={S.previewBtn} onClick={() => setPreviewOpen(true)}>
          Preview welcome screen
        </button>
        <span style={S.note}>
          Overrides replace the shipped English text everywhere it appears. Clearing a field
          restores the default. Preview writes no user flags — safe to open anytime.
        </span>
      </div>

      {error && <div style={{ ...S.note, color: '#dc2626' }}>{error}</div>}

      {CATALOG.map((entry) => {
        const shipped = String(tEn(entry.i18nKey as any) ?? '');
        const draft = drafts[entry.key] ?? '';
        const hasOverride = overrides.has(entry.key);
        return (
          <div key={entry.key} style={S.row}>
            <div style={S.rowHead}>
              <span style={S.label}>{entry.label}</span>
              <span style={S.keyText}>{entry.key}</span>
            </div>
            <p style={S.defaultText}>Default: {shipped}</p>
            {entry.multiline ? (
              <textarea
                style={{ ...S.input, minHeight: 88, resize: 'vertical' }}
                value={draft}
                placeholder="No override — the default above is live."
                onChange={(e) => setDrafts((p) => ({ ...p, [entry.key]: e.target.value }))}
              />
            ) : (
              <input
                style={S.input}
                value={draft}
                placeholder="No override — the default above is live."
                onChange={(e) => setDrafts((p) => ({ ...p, [entry.key]: e.target.value }))}
              />
            )}
            {entry.hint && <div style={S.hint}>{entry.hint}</div>}
            <div style={S.actions}>
              <button
                type="button"
                style={S.saveBtn}
                disabled={busyKey === entry.key}
                onClick={() => save(entry.key, draft)}
              >
                {busyKey === entry.key ? 'Saving…' : 'Save override'}
              </button>
              {hasOverride && (
                <button
                  type="button"
                  style={S.clearBtn}
                  disabled={busyKey === entry.key}
                  onClick={() => {
                    setDrafts((p) => ({ ...p, [entry.key]: '' }));
                    save(entry.key, '');
                  }}
                >
                  Clear (use default)
                </button>
              )}
              {savedKey === entry.key && <span style={S.status}>Saved</span>}
            </div>
          </div>
        );
      })}

      {/* No-signup test path: the REAL modal with the current effective copy;
          no flags written (preview host, navigateOnCta off). */}
      <WelcomeScreenModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        navigateOnCta={false}
      />
    </div>
  );
}
